import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Editor from '@monaco-editor/react';
import type { editor } from 'monaco-editor';
import {
  ArrowLeft,
  Save,
  Upload,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertCircle,
} from 'lucide-react';
import { useGlobalAssistant } from '@/contexts/GlobalAssistantContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@/components/ui/resizable';
import { useToast } from '@/hooks/use-toast';
import { schemaApi, type JsonSchema, type SchemaItem } from '@/services/schemaApi';
import { SchemaVisualBuilder } from '@/components/schemas/SchemaVisualBuilder';

const EDITOR_OPTIONS: editor.IStandaloneEditorConstructionOptions = {
  minimap: { enabled: false },
  lineNumbers: 'on',
  wordWrap: 'on',
  fontSize: 13,
  fontFamily: '"Fira Code", "Monaco", "Menlo", monospace',
  scrollBeyondLastLine: false,
  automaticLayout: true,
  tabSize: 2,
  insertSpaces: true,
  readOnly: true, // Preview is read-only
  renderWhitespace: 'selection',
};

export function SchemaEditorPage() {
  const { schemaId } = useParams<{ schemaId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { registerPageContext } = useGlobalAssistant();
  const isNew = schemaId === 'new';

  // Schema state
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [item, setItem] = useState<SchemaItem | null>(null);
  const [schema, setSchema] = useState<JsonSchema>(schemaApi.createEmptySchema());
  const [originalSchema, setOriginalSchema] = useState<string>('');

  // Form state
  const [name, setName] = useState('');

  // Validation state
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Compute if there are unsaved changes
  const hasChanges = JSON.stringify(schema) !== originalSchema;

  // Register schema editor context for global assistant
  useEffect(() => {
    registerPageContext({
      contextType: 'schemas',
      schemas: {
        current_schema_id: schemaId,
        schema_name: name,
        schema_content: JSON.stringify(schema, null, 2),
        validation_errors: validationErrors,
        has_unsaved_changes: hasChanges,
      },
    });
  }, [registerPageContext, schemaId, name, schema, validationErrors, hasChanges]);

  // Load schema data
  useEffect(() => {
    if (isNew) {
      setSchema(schemaApi.createEmptySchema());
      setOriginalSchema(JSON.stringify(schemaApi.createEmptySchema()));
      setLoading(false);
      return;
    }

    async function loadSchema() {
      try {
        setLoading(true);

        // Load item metadata and content in parallel
        const [itemData, content] = await Promise.all([
          schemaApi.get(schemaId!),
          schemaApi.getContent(schemaId!),
        ]);

        setItem(itemData);
        setName(itemData.name);
        setSchema(content);
        setOriginalSchema(JSON.stringify(content));
      } catch (error) {
        toast({
          title: 'Error',
          description: 'Failed to load schema',
          variant: 'destructive',
        });
        navigate('/developer/schemas');
      } finally {
        setLoading(false);
      }
    }
    loadSchema();
  }, [schemaId, isNew, navigate, toast]);

  // Validate schema on change
  useEffect(() => {
    const validate = async () => {
      const result = await schemaApi.validate(schema);
      setValidationErrors(result.errors);
    };
    validate();
  }, [schema]);

  // Handle schema changes from visual builder
  const handleSchemaChange = useCallback((newSchema: JsonSchema) => {
    setSchema(newSchema);
  }, []);

  // Handle save
  const handleSave = useCallback(async () => {
    if (!name.trim()) {
      toast({
        title: 'Error',
        description: 'Schema name is required',
        variant: 'destructive',
      });
      return;
    }

    try {
      setSaving(true);

      if (isNew) {
        const created = await schemaApi.create(name, schema);
        toast({
          title: 'Created',
          description: 'Schema created successfully',
        });
        navigate(`/developer/schemas/${created.id}`, { replace: true });
      } else {
        await schemaApi.update(schemaId!, schema, 'Updated via visual builder');
        setOriginalSchema(JSON.stringify(schema));
        toast({
          title: 'Saved',
          description: 'Schema saved as draft',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to save schema',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  }, [name, schema, isNew, schemaId, navigate, toast]);

  // Handle publish
  const handlePublish = useCallback(async () => {
    if (isNew) {
      await handleSave();
      return;
    }

    try {
      setPublishing(true);

      // Save first if there are changes
      if (hasChanges) {
        await schemaApi.update(schemaId!, schema, 'Published via visual builder');
        setOriginalSchema(JSON.stringify(schema));
      }

      await schemaApi.publish(schemaId!);
      toast({
        title: 'Published',
        description: 'Schema published successfully',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to publish schema',
        variant: 'destructive',
      });
    } finally {
      setPublishing(false);
    }
  }, [isNew, hasChanges, schemaId, schema, handleSave, toast]);

  // Format JSON for preview
  const jsonPreview = JSON.stringify(schema, null, 2);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-3.5rem)]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-3.5rem)] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 border-b bg-background shrink-0">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/developer/schemas')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-2">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="schema_name"
              className="w-64 font-mono"
            />
            {hasChanges && (
              <Badge variant="secondary">Unsaved</Badge>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Validation Status */}
          {validationErrors.length === 0 ? (
            <Badge variant="outline" className="text-green-600 border-green-600">
              <CheckCircle2 className="h-3 w-3 mr-1" />
              Valid
            </Badge>
          ) : (
            <Badge variant="outline" className="text-destructive border-destructive">
              <XCircle className="h-3 w-3 mr-1" />
              {validationErrors.length} error(s)
            </Badge>
          )}

          {/* Save Button */}
          <Button variant="outline" onClick={handleSave} disabled={saving || !hasChanges}>
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save Draft
          </Button>

          {/* Publish Button */}
          <Button onClick={handlePublish} disabled={publishing || validationErrors.length > 0}>
            {publishing ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Upload className="h-4 w-4 mr-2" />
            )}
            {isNew ? 'Create' : 'Publish'}
          </Button>
        </div>
      </div>

      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <div className="px-4 py-2 border-b bg-destructive/10">
          <div className="space-y-1">
            {validationErrors.map((error, i) => (
              <div key={i} className="flex items-start gap-2 text-sm text-destructive">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Main Content */}
      <ResizablePanelGroup direction="horizontal" className="flex-1">
        {/* Left Panel - Visual Builder */}
        <ResizablePanel defaultSize={55} minSize={40}>
          <div className="h-full border-r">
            <SchemaVisualBuilder schema={schema} onChange={handleSchemaChange} />
          </div>
        </ResizablePanel>

        <ResizableHandle />

        {/* Right Panel - JSON Preview */}
        <ResizablePanel defaultSize={45} minSize={30}>
          <div className="h-full flex flex-col">
            <div className="px-4 py-2 border-b flex items-center justify-between bg-muted/30">
              <span className="text-sm font-medium">JSON Schema Preview</span>
              <Badge variant="outline" className="text-xs">Read-only</Badge>
            </div>
            <div className="flex-1">
              <Editor
                height="100%"
                language="json"
                value={jsonPreview}
                options={EDITOR_OPTIONS}
                theme="vs"
                loading={
                  <div className="flex items-center justify-center h-full text-muted-foreground">
                    Loading preview...
                  </div>
                }
              />
            </div>
          </div>
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  );
}

export default SchemaEditorPage;
